import { CantonalMapSvg } from '@/components/CantonalMapSvg'
import * as D from '@/domain'
import { useMeasure } from '@/hooks/use-measure'
import { useEnv } from '@/lib/env'
import { BallotVariant, naySecondary, yeaSecondary } from '@/materials/color'
import * as Icon from '@/materials/icons/all'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { css } from 'styled-components/macro'
import { BallotContext, defaultBallotContextValue } from '../../../contexts/BallotContext'
import { LiveUpdate } from '@/components/LiveUpdate'
import * as F from 'fp-ts'
import { OutcomeLabelFragment } from '@/components/OutcomeLabelFragment'

const Root = 'a'

interface Props extends React.ComponentPropsWithoutRef<typeof Root> {
  popularVoteId: string
  name: React.ReactNode
  ballotVariant: BallotVariant
  sideLabels: D.SideLabels
  cantonState: D.CantonState
  municipalities: Array<D.MunicipalityState>
}

function Tile(props: Props) {
  const { intl } = useEnv()

  const { popularVoteId, name, ballotVariant, sideLabels, cantonState, municipalities, ...rest } = props

  interface S {
    result: React.ReactNode
    icon: React.ReactNode
    label: React.ReactNode
    color?: string
  }

  const resultLabel = (outcome: D.Outcome) =>
    F.function.pipe(
      F.option.fromNullable(sideLabels),
      F.option.chain((sideLabels) => F.option.fromNullable(sideLabels[outcome])),
      F.option.chain((mls) => F.option.fromNullable(D.pickMultiLocaleString(intl, mls, undefined))),
      F.option.getOrElseW(() => <OutcomeLabelFragment ballotVariant={ballotVariant} outcome={outcome} />),
    )

  const { color, result, icon, label } = D.caseBallotState<S>({
    pre: { result: null, icon: null, label: null },
    inProgress: {
      result: null,
      icon: null,
      label: (
        <FormattedMessage
          id="components.CantonalBallotsSummary.Tile.label.in-progress"
          defaultMessage="Noch kein Resultat"
        />
      ),
    },
    trend: () => ({ result: 'RESULT', icon: <Icon.Checkmark24 />, label: 'LABEL' }),
    projection: ({ projection }) => {
      const outcome = D.outcomeFromPercent(projection)

      return {
        result: (
          <FormattedMessage
            id="components.CantonalBallotsSummary.Tile.result.projection"
            defaultMessage="{string} {label}"
            values={{
              string: String(
                D.caseOutcome({
                  yea: D.unPercent(projection),
                  nay: 1 - D.unPercent(projection),
                })(outcome),
              ).replace('.', ','),
              label: resultLabel(outcome),
            }}
          />
        ),
        icon: (
          <div style={{ marginRight: '10px' }}>
            <LiveUpdate />
          </div>
        ),
        label: (
          <FormattedMessage id="components.CantonalBallotsSummary.Tile.projection" defaultMessage="Liveprognose" />
        ),
        color: D.caseOutcome({
          yea: { default: 'var(--blue-main)', tieBreak: yeaSecondary }[ballotVariant],
          nay: { default: 'var(--red-main)', tieBreak: naySecondary }[ballotVariant],
        })(outcome),
      }
    },
    finalResult: ({ finalResult }) => {
      const outcome = D.resultOutcome(finalResult)

      return {
        result: (
          <FormattedMessage
            id="components.CantonalBallotsSummary.Tile.result.finalResult"
            defaultMessage="{value, number, percent1} {label}"
            values={{
              value: D.caseOutcome({
                yea: D.unPercent(finalResult.votes.yea.percent),
                nay: D.unPercent(finalResult.votes.nay.percent),
              })(outcome),
              label: resultLabel(outcome),
            }}
          />
        ),
        icon: <Icon.Checkmark24 />,
        label: <FormattedMessage id="components.CantonalBallotsSummary.Tile.result" defaultMessage="Schlussresultat" />,
        color: D.caseOutcome({
          yea: { default: 'var(--blue-main)', tieBreak: yeaSecondary }[ballotVariant],
          nay: { default: 'var(--red-main)', tieBreak: naySecondary }[ballotVariant],
        })(outcome),
      }
    },
  })(cantonState.state)

  const [{ ref: mapContainerRef }, rect] = useMeasure()

  return (
    <BallotContext.Provider value={{ ...defaultBallotContextValue, ballotVariant }}>
      <Root css={[styles.root] as any} style={{ '--result-color': color } as any} {...rest}>
        <div css={styles.name as any}>{name}</div>
        <div css={styles.result as any}>{result}</div>
        <div css={styles.map as any} ref={mapContainerRef}>
          <CantonalMapSvg
            date={new Date(Date.parse(popularVoteId))}
            width={rect.width}
            cantonState={cantonState}
            municipalityData={municipalities}
          />
        </div>
        <div css={styles.label as any}>
          {icon}
          {label}
        </div>
      </Root>
    </BallotContext.Provider>
  )
}

export default Tile

const styles = {
  root: css`
    display: grid;
    grid-template-areas: 'name name' 'result map' 'status map';
    grid-template-columns: 1fr max-content;
    grid-template-rows: min-content 1fr min-content;

    @media only screen and (min-width: 700px) {
      grid-template-areas: 'name name' 'result result' 'map map' 'status status';
      grid-template-rows: min-content min-content 1fr min-content;
    }

    color: inherit;
    text-decoration: none;

    & > * {
      transition: opacity 0.12s;
    }
    &:hover > * {
      opacity: 0.8;
    }
  `,

  name: css`
    grid-area: name;

    font-family: var(--font-sans-serif);
    font-weight: 700;
    font-size: 15px;
    line-height: 1.4;

    margin-bottom: 5px;

    overflow-wrap: anywhere;
  `,

  result: css`
    grid-area: result;

    color: var(--result-color);

    font-family: var(--font-serif, var(--font-title));
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1.1;
  `,

  map: css`
    grid-area: map;
    align-self: end;

    height: 120px;
    display: grid;
    place-items: center;

    margin: 10px 0;
  `,

  label: css`
    grid-area: status;
    align-self: end;

    display: flex;
    align-items: center;

    font-family: var(--font-sans-serif);
    font-weight: 400;
    font-size: 15px;
    line-height: 1.4;

    & > svg {
      margin: -3px 3px 0 -2px;
    }
  `,
}
